import {udLink} from '@udemy/ud-data';

import {ValuePropProps} from '../components/value-prop/value-prop.react-component';

export interface ValuePropConfigItem {
    id: string;
    heading: string;
    /** Optional intro paragraph. Some rows lead straight from the heading into `listItems`. */
    content?: string;
    imgSrc: ValuePropProps['imgSrc'];
    slogan?: string;
    listItems?: string[];
}

/**
 * Returns value prop content for the Personal Plan landing page.
 * Config selection follows the same flag precedence as the page body:
 * complete revamp wins over partial revamp, which wins over legacy.
 */
export function getValuePropsConfig(
    gettext: (key: string) => string,
    isPPLPRevamp: boolean,
    isPPLPCompleteRevamp = false,
): ValuePropConfigItem[] {
    const baseConfig: ValuePropConfigItem[] = [
        {
            id: 'current',
            slogan: gettext('Current'),
            heading: gettext('Cutting-edge skills to keep you sharp'),
            content: gettext(
                'Learn confidently with up-to-date courses covering in-demand topics like AI for any role, cloud computing certifications, web development, productivity, leadership, design, digital marketing, and more.',
            ),
            imgSrc: {
                mobileSrc: udLink.toStorageStaticAsset(
                    'consumer-subscription/pillars/pillars-1-mobile-v2.jpg',
                ),
                desktopSrc: udLink.toStorageStaticAsset(
                    'consumer-subscription/pillars/pillars-1-desktop-v2.jpg',
                ),
                desktopSrc2x: udLink.toStorageStaticAsset(
                    'consumer-subscription/pillars/pillars-1-desktop-2x-v2.jpg',
                ),
            },
        },
        {
            id: 'flexible',
            slogan: gettext('Flexible'),
            heading: gettext('Freedom to explore and discover'),
            content: gettext(
                'Test drive a new subject, switch between courses, or pick and choose the lessons that best fit your needs. Personal Plan gives you the power to control what and how you learn. Plus use the Udemy AI Assistant to get instant answers to your questions while you learn.',
            ),
            imgSrc: {
                mobileSrc: udLink.toStorageStaticAsset(
                    'consumer-subscription/pillars/pillars-2-mobile-v2.jpg',
                ),
                desktopSrc: udLink.toStorageStaticAsset(
                    'consumer-subscription/pillars/pillars-2-desktop-v2.jpg',
                ),
                desktopSrc2x: udLink.toStorageStaticAsset(
                    'consumer-subscription/pillars/pillars-2-desktop-2x-v2.jpg',
                ),
            },
        },
        {
            id: 'effective',
            slogan: gettext('Effective'),
            heading: gettext('Learning designed to help you start doing'),
            content: gettext(
                'Gain knowledge through practice with AI Role Play simulations, labs, coding exercises, and certification exam practice tests. Once you finish a course, strut your new skills with a certificate of completion from Udemy or issuers like AWS, Microsoft, Google, CompTIA, and PMI.',
            ),
            imgSrc: {
                mobileSrc: udLink.toStorageStaticAsset(
                    'consumer-subscription/pillars/pillars-3-mobile-v2.jpg',
                ),
                desktopSrc: udLink.toStorageStaticAsset(
                    'consumer-subscription/pillars/pillars-3-desktop-v2.jpg',
                ),
                desktopSrc2x: udLink.toStorageStaticAsset(
                    'consumer-subscription/pillars/pillars-3-desktop-2x-v2.jpg',
                ),
            },
        },
    ];

    const completeRevampConfig: ValuePropConfigItem[] = [
        {
            id: 'skills',
            heading: gettext('Build the AI skills employers are hiring for'),
            content: gettext(
                'Learn to change how work gets done with 2,000+ courses on ChatGPT, Claude, Copilot, Gemini, and more.',
            ),
            listItems: [
                gettext(
                    'Move from AI fluency to AI mastery — building agents, automating workflows and more',
                ),
                gettext(
                    'Refreshed when new models launch and tools change to keep you learning at the pace of change',
                ),
                gettext('Go beyond simple prompting to real workflows'),
                gettext(
                    'Plan projects, brainstorm ideas and analyze data in ways you can use daily',
                ),
            ],
            imgSrc: {
                mobileSrc:
                    'https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/personal-plan/personal-plan-value-prop/skills-mobile-1x.webp',
                desktopSrc:
                    'https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/personal-plan/personal-plan-value-prop/skills-1x.webp',
                desktopSrc2x:
                    'https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/personal-plan/personal-plan-value-prop/skills-2x.webp',
            },
        },
        {
            id: 'practice',
            heading: gettext("See where you stand. Practice until you're ready."),
            content: gettext(
                'Assess your skills across AI, cloud, cybersecurity, engineering, and more to identify your strengths and gaps. Get personalized recommendations for where to focus next, then build your skills through courses, AI Role Play, and hands-on labs — practicing real-world scenarios before you face them for real.',
            ),
            imgSrc: {
                mobileSrc:
                    'https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/personal-plan/personal-plan-value-prop/practice-mobile-1x.webp',
                desktopSrc:
                    'https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/personal-plan/personal-plan-value-prop/practice-1x.webp',
                desktopSrc2x:
                    'https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/personal-plan/personal-plan-value-prop/practice-2x.webp',
            },
        },
        {
            id: 'plan',
            heading: gettext('The best of Udemy, all in one Plan'),
            // This row goes straight from the heading into the list, with no intro paragraph.
            listItems: [
                gettext(
                    'Always current — new content added monthly so your skills keep pace with the tools and trends.',
                ),
                gettext(
                    'Always more — multiple top rated courses per skill in business, marketing, technology and more',
                ),
                gettext(
                    "Always adaptable — Follow one subject deeply, or pivot to something new. Find the right instructor to fit your learning style. It's all in your plan.",
                ),
            ],
            imgSrc: {
                mobileSrc:
                    'https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/personal-plan/personal-plan-value-prop/plan-mobile-1x.webp',
                desktopSrc:
                    'https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/personal-plan/personal-plan-value-prop/plan-1x.webp',
                desktopSrc2x:
                    'https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/personal-plan/personal-plan-value-prop/plan-2x.webp',
            },
        },
    ];

    const revampConfig: ValuePropConfigItem[] = [
        {
            id: 'career',
            slogan: gettext('CAREER'),
            heading: gettext('Careers reimagined'),
            content: gettext(
                'Start or advance your career in graphic design, web dev, digital marketing, and more with unlimited access to 26,000+ courses — plus curated learning with Career Accelerators',
            ),
            imgSrc: {
                mobileSrc:
                    'https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/personal-plan/personal-plan-value-prop/PersonalPlanValuePropMobile_1.png',
                desktopSrc:
                    'https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/personal-plan/personal-plan-value-prop/PersonalPlanValuePropDesktop_1.png',
                desktopSrc2x:
                    'https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/personal-plan/personal-plan-value-prop/PersonalPlanValuePropDesktop_1.png',
            },
        },
        {
            id: 'learn',
            slogan: gettext('LEARN'),
            heading: gettext('AI skills at the ready'),
            content: gettext(
                'Keep ahead with the latest in AI skills including ChatGPT, Microsoft CoPilot, Midjourney, machine learning, deep learning, and more with over 1,300 AI courses included',
            ),
            imgSrc: {
                mobileSrc:
                    'https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/personal-plan/personal-plan-value-prop/PersonalPlanValuePropMobile_2.png',
                desktopSrc:
                    'https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/personal-plan/personal-plan-value-prop/PersonalPlanValuePropDesktop_2.png',
                desktopSrc2x:
                    'https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/personal-plan/personal-plan-value-prop/PersonalPlanValuePropDesktop_2.png',
            },
        },
        {
            id: 'certification',
            slogan: gettext('CERTIFICATION'),
            heading: gettext('Certification prep. Career advancement.'),
            content: gettext(
                'Prepare for exams in CompTIA, AWS, PMP, and other in-demand certifications, plus take the next step with exam vouchers',
            ),
            imgSrc: {
                mobileSrc:
                    'https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/personal-plan/personal-plan-value-prop/PersonalPlanValuePropMobile_3.png',
                desktopSrc:
                    'https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/personal-plan/personal-plan-value-prop/PersonalPlanValuePropDesktop_3.png',
                desktopSrc2x:
                    'https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/personal-plan/personal-plan-value-prop/PersonalPlanValuePropDesktop_3.png',
            },
        },
        {
            id: 'practice',
            slogan: gettext('PRACTICE'),
            heading: gettext('AI-powered learning for the real world'),
            content: gettext(
                'Upskill smarter and faster with guided learning from Udemy AI Assistant, conversation coaching from Role Play, and coding exercises that develop your technical skills',
            ),
            imgSrc: {
                mobileSrc:
                    'https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/personal-plan/personal-plan-value-prop/PersonalPlanValuePropMobile_4.png',
                desktopSrc:
                    'https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/personal-plan/personal-plan-value-prop/PersonalPlanValuePropDesktop_4.png',
                desktopSrc2x:
                    'https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/personal-plan/personal-plan-value-prop/PersonalPlanValuePropDesktop_4.png',
            },
        },
    ];

    return isPPLPCompleteRevamp ? completeRevampConfig : isPPLPRevamp ? revampConfig : baseConfig;
}
